import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import {
  PageHeader, Table, Th, Td, TrEmpty, Badge, LoadingState, ErrorState,
} from '../components/ui';

interface AuditEntry {
  id: string;
  actorEmail: string;
  action: string;
  resourceType: string;
  resourceId: string | null;
  entityId: string | null;
  timestamp: string;
}

function formatTs(ts: string) {
  const d = new Date(ts);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    + ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export function AuditLog() {
  const { data, isLoading, error } = useQuery<{ entries: AuditEntry[] }>({
    queryKey: ['admin', 'audit-log'],
    queryFn: () => api.get('/audit-log?limit=100'),
  });

  const entries = data?.entries ?? [];

  return (
    <div>
      <PageHeader
        title="Audit Log"
        subtitle={data ? `Last ${entries.length} entries` : 'Platform admin activity'}
      />

      {isLoading && <LoadingState />}
      {error && <ErrorState message="Error loading audit log" />}

      {!isLoading && !error && (
        <Table>
          <thead>
            <tr>
              <Th>Timestamp</Th>
              <Th>Actor</Th>
              <Th>Action</Th>
              <Th>Resource</Th>
              <Th>Entity</Th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 ? (
              <TrEmpty cols={5} message="No audit entries" />
            ) : (
              entries.map(e => (
                <tr key={e.id} className="hover:bg-white/[0.02] transition-colors">
                  <Td className="font-mono text-xs text-gray-400 whitespace-nowrap">
                    {formatTs(e.timestamp)}
                  </Td>
                  <Td className="text-xs">{e.actorEmail}</Td>
                  <Td>
                    <Badge variant="mono">{e.action}</Badge>
                  </Td>
                  <Td className="text-xs text-gray-300">
                    <span className="font-medium">{e.resourceType}</span>
                    {e.resourceId && (
                      <span className="text-gray-500 font-mono"> / {e.resourceId}</span>
                    )}
                  </Td>
                  <Td className="font-mono text-[11px] text-gray-500">
                    {e.entityId ?? '—'}
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      )}
    </div>
  );
}
